import './clients.scss';

import { notification } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { SystemNotifications } from '@/components/Shared/entities';
import { ROUTES } from '@/components/Shared/routes/routes';
import { Input, Table } from '@/components/Shared/ui';
import useBaseFilters from '@/hooks/useBaseFilters';
import SearchIcon from '@/media/icons/search.svg?react';
import { generateRandomId } from '@/services';
import { useAppDispatch } from '@/store';
import { setHeaderConfig } from '@/store/app.slice';
import { getClientByIin } from '@/store/clients.slice';

import { clientsMapperFromApi } from '../helpers/clientsMapper';
import type { TClient } from '../types';
import { ClientsMobile } from './entities';
import { clientsColumns } from './widgets';

const Clients = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [clients, setClients] = useState<TClient[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const prevNotificationKey = useRef<string | undefined>(undefined);

  const { paginationData, searchValue, debouncedSearchValue, handleSearch } =
    useBaseFilters({ searchStorageKey: 'client-iin-search' });

  const handlePageChange = (currentPage: number, itemsPerPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(currentPage));
    params.set('per-page', String(itemsPerPage));
    setSearchParams(params);
  };

  const getClient = useCallback(async () => {
    if (searchValue.length < 12) return;

    setIsLoading(true);
    const response = await dispatch(getClientByIin({ iin: searchValue }));
    setIsLoading(false);

    if (getClientByIin.fulfilled.match(response)) {
      const client = clientsMapperFromApi(response.payload);
      setClients([client]);
    } else {
      setClients([]);
    }
  }, [dispatch, searchValue]);

  const handleShowClientDetails = (client: TClient) => {
    navigate(ROUTES.client(client.uuid));
  };

  useEffect(() => {
    if (debouncedSearchValue && debouncedSearchValue.length === 12) getClient();
    else setClients([]);
  }, [debouncedSearchValue]);

  useEffect(() => {
    dispatch(
      setHeaderConfig({
        title: 'Клиенты',
        helmetTitle: 'Клиенты'
      })
    );
    return () => {
      dispatch(setHeaderConfig({}));
    };
  }, []);

  return (
    <div className="clients">
      <SystemNotifications />

      <div className="clients__content">
        <div className="clients__header">
          <h2 className="clients__title">Количество: {clients?.length || 0}</h2>

          <div className="clients__search">
            <Input
              type="tel"
              placeholder="Поиск по ИИН"
              value={searchValue}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                if (value.length > 12) {
                  notification.destroy(prevNotificationKey.current);
                  prevNotificationKey.current =
                    generateRandomId('iin-length-error');
                  notification.warning({
                    message: 'Максимальная длина ИИН 12 символов',
                    key: prevNotificationKey.current,
                    duration: 2
                  });
                  return;
                }
                handleSearch(value);
              }}
              endContent={
                <span className="clients__search-icon">
                  <SearchIcon />
                </span>
              }
              inputMode="numeric"
            />
          </div>
        </div>

        {/* Сообщение при отсутствии данных после поиска */}
        {debouncedSearchValue.length === 12 &&
          clients.length === 0 &&
          !isLoading && (
            <div className="clients__no-results">
              По запросу <strong>{debouncedSearchValue}</strong> ничего не
              найдено
            </div>
          )}
        <div className="clients__table">
          <Table
            columns={clientsColumns}
            dataSource={clients.length > 0 && clients[0].uuid ? clients : []}
            rowKey="uuid"
            rowLink={(item) => ROUTES.client(item.uuid)}
            pagination={{
              total: clients.length,
              pageSize: paginationData.itemsPerPage,
              current: paginationData.currentPage,
              onChange: handlePageChange
            }}
            loading={isLoading}
            onRowClick={(r) => {
              handleShowClientDetails(r);
            }}
          />
        </div>
        <div className="clients__mobile-data">
          <ClientsMobile
            clients={clients}
            isLoading={isLoading}
            pagination={{
              current: paginationData.currentPage,
              pageSize: paginationData.itemsPerPage,
              total: clients.length
            }}
            onPageChange={handlePageChange}
            onClick={handleShowClientDetails}
          />
        </div>
      </div>
    </div>
  );
};

export default Clients;
